import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { KnowledgeBaseItem } from "@/services/knowledgeBaseService";
import DOMPurify from "dompurify";

interface DocumentViewerModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    document: KnowledgeBaseItem | null;
}

/**
 * Read-only modal for viewing shared space documents (owned by other users).
 * Uses document data passed as prop — no API fetch, so it works for shared docs.
 */
export function DocumentViewerModal({
    open,
    onOpenChange,
    document: doc,
}: DocumentViewerModalProps) {
    const sanitizedContent = doc?.content
        ? DOMPurify.sanitize(doc.content)
        : "";

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col p-0 gap-0">
                <DialogHeader className="px-6 pt-6 pb-4 shrink-0 border-b">
                    <DialogTitle className="pr-8">{doc?.title || "Document"}</DialogTitle>
                    <DialogDescription>
                        Shared document — read only. You can view but not edit.
                    </DialogDescription>
                </DialogHeader>
                <ScrollArea className="flex-1 min-h-0 overflow-auto">
                    <div className="p-4">
                        {sanitizedContent ? (
                            <div
                                className="prose prose-sm dark:prose-invert max-w-none"
                                dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                            />
                        ) : (
                            <p className="text-muted-foreground text-sm">No content.</p>
                        )}
                    </div>
                </ScrollArea>
            </DialogContent>
        </Dialog>
    );
}
